import { useState, useEffect } from 'react';

/**
 * Hook to manage determinate progress animation.
 * Returns the current progress value, animating from 0 to 99.5% for loading states.
 *
 * @param mode - Whether the loader is determinate or indeterminate
 * @param initialProgress - Initial progress value (0-100)
 * @param speed - Animation speed multiplier
 * @returns Current progress value
 */
export const useDeterminateProgress = (
  mode: 'determinate' | 'indeterminate',
  initialProgress: number,
  speed: number,
): number => {
  const [progress, setProgress] = useState(initialProgress);

  useEffect(() => {
    // Only animate progress in determinate mode
    if (mode !== 'determinate') {
      return;
    }

    // If initial progress is 100, don't animate
    if (initialProgress >= 100) {
      setProgress(100);
      return;
    }

    let timer: NodeJS.Timeout;

    const animateProgress = () => {
      setProgress((prevProgress) => {
        if (prevProgress >= 100) return 100;

        // Exponential slowdown as we approach 100%
        const remainingProgress = 100 - prevProgress;
        const increment = Math.max(0.1, remainingProgress * 0.03 * speed);

        return Math.min(prevProgress + increment, 99.5);
      });
    };

    timer = setInterval(animateProgress, 50);

    return () => {
      clearInterval(timer);
    };
  }, [mode, initialProgress, speed]);

  // Reset progress when initial progress changes
  useEffect(() => {
    setProgress(initialProgress);
  }, [initialProgress]);

  return progress;
};
